/**
 * <carousel-item>
 * Encapsulates slide styling. 
 * Automatically adjusts width via the parent's CSS variable.
 */
class CarouselItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  // Monitor these attributes for changes
  static get observedAttributes() {
    return ['btn-link', 'btn-text', 'label'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    const btnLink = this.getAttribute('btn-link');
    const btnText = this.getAttribute('btn-text') || 'Learn More';
    const label = this.getAttribute('label');
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: var(--item-width, 80vw);
          height: 400px;
          flex-shrink: 0;
          overflow: hidden;
          opacity: 1;
          transition: opacity 0.6s ease, transform 0.6s ease, width 0.4s cubic-bezier(0.23, 1, 0.32, 1);
          border-radius: 12px;
          /* GPU Acceleration */
          backface-visibility: hidden;
          transform: translateZ(0);
          will-change: opacity, transform;
          border: 1px solid var(--yellow);
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }

        @media (min-width: 1024px) {
          :host { height: 800px; }
        }

        :host(.active) {
          opacity: 1;
        }

        ::slotted(picture) {
          display: block;
          width: 100%;
          height: 100%;
        }

        ::slotted(img), ::slotted(picture) img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          pointer-events: none;
        }

        ::slotted(*) {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Overlay Styles */
        .overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1rem;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          color: white;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;
        }

        @media (min-width: 1024px) {
          .overlay{ padding: 2rem; }
        }

        .label {
          font-family: sans-serif;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .btn {
            color: #119466;
            background-color: #3898ec00;
            border: 1px solid #119466;
            border-radius: 3px;
            margin-top: 20px;
            font-family: Helvetica, sans-serif;
            font-size: 14px;
            text-decoration: none;
            padding: 10px;
            line-height: 15px;
        }

        .btn:hover {
        color: var(--yellow);
          background-color: #119466;
        }

      </style>
      <slot></slot>
      ${(label || btnLink) ? `
        <div class="overlay">
          ${label ? `<span class="label">${label}</span>` : ''}
          ${btnLink ? `<a href="${btnLink}" class="btn">${btnText}</a>` : ''}
        </div>
      ` : ''}
    `;
  }
}

/**
 * <infinite-carousel>
 * Manages layout, infinite cloning, and responsive width.
 */
class InfiniteCarousel extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.index = 1;
    this.gap = 10;
    this.isDragging = false;
    this.startX = 0;
    this.prevX = 0;
    this.timer = null;
  }

  static get observedAttributes() {
    return ['no-auto'];
  }

  connectedCallback() {
    const items = Array.from(this.querySelectorAll('carousel-item'));
    this.realCount = items.length;

    if (this.realCount === 0) return;

    // Layout configuration
    const isSingle = this.realCount === 1;
    this.index = isSingle ? 0 : 1; 
    const slideWidthVw = isSingle ? 95 : 80;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100vw;
          overflow: hidden;
          position: relative;
          touch-action: ${isSingle ? 'none' : 'pan-y'};
          --item-width: ${slideWidthVw}vw;
        }

        .track {
          display: flex;
          gap: ${this.gap}px;
          width: max-content;
          cursor: ${isSingle ? 'default' : 'grab'};
          will-change: transform;
          transform: translate3d(0, 0, 0);
        }

        .nav {
          display: ${isSingle ? 'none' : 'flex'};
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          width: 44px;
          height: 44px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-size: 1.2rem;
          opacity:0;
        height: 100%; 
        width: 50px;
        }

        .prev { left: 5px; }
        .next { right: 5px;   }

        #prefix, #suffix { display: contents; }
      </style>

      <button class="nav prev" aria-label="Previous">❮</button>
      <button class="nav next" aria-label="Next">❯</button>

      <div class="track" id="track">
        <div id="prefix"></div>
        <slot></slot>
        <div id="suffix"></div>
      </div>
    `;

    this.track = this.shadowRoot.getElementById('track');

    if (!isSingle) {
      // Create deep clones for infinite effect
      const firstClone = items[0].cloneNode(true);
      const lastClone = items[items.length - 1].cloneNode(true);
      this.shadowRoot.getElementById('prefix').appendChild(lastClone);
      this.shadowRoot.getElementById('suffix').appendChild(firstClone);
      
      this.setupEvents();
      if (!this.hasAttribute('no-auto')) this.startAuto();
    }

    // Initialize position
    setTimeout(() => this.update(false), 50);
  }

  update(animate = true) {
    const isSingle = this.realCount === 1;
    const slideWidthVw = isSingle ? 95 : 80;
    const itemWidthPx = window.innerWidth * (slideWidthVw / 100);
    const centerOffset = (window.innerWidth - itemWidthPx) / 2;
    
    // Calculate final X position
    const x = isSingle ? centerOffset : -(this.index * (itemWidthPx + this.gap)) + centerOffset;

    this.track.style.transition = animate ? 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)' : 'none';
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
    this.prevX = x;

    this.updateActiveClasses();
  }

  updateActiveClasses() {
    const realItems = Array.from(this.querySelectorAll('carousel-item'));
    const prefix = this.shadowRoot.getElementById('prefix').firstElementChild;
    const suffix = this.shadowRoot.getElementById('suffix').firstElementChild;
    const all = [prefix, ...realItems, suffix].filter(Boolean);

    all.forEach((item, i) => {
      // In single mode, the item is always active
      const isActive = this.realCount === 1 ? true : i === this.index;
      item.classList.toggle('active', isActive);
    });
  }

  setupEvents() {
    const prevBtn = this.shadowRoot.querySelector('.prev');
    const nextBtn = this.shadowRoot.querySelector('.next');

    prevBtn.onclick = () => { this.index--; this.update(); this.stopAuto(); };
    nextBtn.onclick = () => { this.index++; this.update(); this.stopAuto(); };

    const start = (e) => {
      this.isDragging = true;
      this.startX = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      this.stopAuto();
      this.track.style.transition = 'none';
    };

    const move = (e) => {
      if (!this.isDragging) return;
      const x = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
      const walk = x - this.startX;
      this.track.style.transform = `translate3d(${this.prevX + walk}px, 0, 0)`;
    };

    const end = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      const x = e.type.includes('mouse') ? e.pageX : e.changedTouches[0].clientX;
      const dist = x - this.startX;

      if (Math.abs(dist) > 100) {
        this.index += dist > 0 ? -1 : 1;
      }
      this.update(true);
      if (!this.hasAttribute('no-auto')) this.startAuto();
    };

    this.track.addEventListener('mousedown', start);
    this.track.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);

    this.track.addEventListener('transitionend', () => {
      if (this.index >= this.realCount + 1) {
        this.index = 1;
        this.update(false);
      } else if (this.index <= 0) {
        this.index = this.realCount;
        this.update(false);
      }
    });

    window.addEventListener('resize', () => this.update(false));
  }

  startAuto() {
    this.stopAuto();
    this.timer = setInterval(() => {
      this.index++;
      this.update(true);
    }, 4000);
  }

  stopAuto() {
    clearInterval(this.timer);
  }
}

customElements.define('carousel-item', CarouselItem);
customElements.define('infinite-carousel', InfiniteCarousel);